import { useMemo } from 'react';
import { CheckExistingLocationResponse } from '../../../types/location';
import pluralize from 'pluralize';
import { ErrorInterface } from '../../../types/errors';
import { uniqBy } from 'lodash';

export enum ImportLocationStatus {
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  ERROR = 'ERROR',
}

export enum ImportPhase {
  START,
  END,
}

const generateReportText = (
  count: number,
  total: number,
  action: ImportLocationStatus,
  phase: ImportPhase,
) => {
  if (!count) return null;

  const pluralizedLocations = pluralize('location', total);

  switch (action) {
    case ImportLocationStatus.CREATE:
      return `${count} of ${total} new ${pluralizedLocations} ${
        phase === ImportPhase.START ? 'to be created.' : 'created.'
      }`;
    case ImportLocationStatus.UPDATE:
      return `${count} of ${total} existing ${pluralizedLocations} ${
        phase === ImportPhase.START ? 'to be updated.' : 'updated.'
      }`;
    case ImportLocationStatus.ERROR:
      if (total === 0) return 'No locations were detected in this file.';
      return `${count} of ${total} ${pluralizedLocations} ${
        phase === ImportPhase.START
          ? 'in the file has one or more errors.'
          : "couldn't be created due to errors."
      }`;
  }
};

interface LocationImportStatisticsWithTitleProps {
  title: string;
  uploadedLocationsCount: number;
  locationsReport: CheckExistingLocationResponse | null;
  errors?: ErrorInterface[];
  stateOfImport: ImportPhase;
}

export const LocationImportStatisticsWithTitle = ({
  title,
  uploadedLocationsCount,
  locationsReport,
  errors,
  stateOfImport,
}: LocationImportStatisticsWithTitleProps) => {
  const existingLocationsCount =
    locationsReport?.existing_locations?.length || 0;
  const newLocationsCount = locationsReport?.new_locations?.length || 0;

  const locationsWithErrorsCount = useMemo(
    () => uniqBy(errors, 'locationName')?.length || 0,
    [errors],
  );

  return (
    <div className='mb-4'>
      <p className='text-gray-800 text-sm font-semibold'>{title}</p>

      {locationsWithErrorsCount > 0 ? (
        <p className='text-gray-600 text-sm'>
          {generateReportText(
            locationsWithErrorsCount,
            uploadedLocationsCount,
            ImportLocationStatus.ERROR,
            stateOfImport,
          )}
        </p>
      ) : (
        <>
          <p className='text-gray-600 text-sm'>
            {generateReportText(
              newLocationsCount,
              uploadedLocationsCount,
              ImportLocationStatus.CREATE,
              stateOfImport,
            )}
          </p>
          <p className='text-gray-600 text-sm'>
            {generateReportText(
              existingLocationsCount,
              uploadedLocationsCount,
              ImportLocationStatus.UPDATE,
              stateOfImport,
            )}
          </p>
        </>
      )}
    </div>
  );
};
